/**
 * What the server tells a client has changed — and deliberately nothing about what it
 * changed *to*.
 *
 * Until LPN-27 nothing told anybody anything. Every client read and wrote
 * `/api/user/ui-settings` and `/api/bookmarks` and found out about the rest of the world
 * when somebody pressed `↻`. A bookmark saved on the phone was invisible in the browser
 * until then; a window moved in the browser was invisible on the phone. This is the missing
 * half, and it is the smallest thing that could be.
 *
 * ── Identity, not payload ────────────────────────────────────────────────────
 *
 * An event says *"bookmark 412 changed"*, never *"here is bookmark 412"*. Four reasons, and
 * each of them is a way the other design goes wrong:
 *
 *  - **One arrival path.** A client that re-reads uses the API it already has —
 *    `StorageRepository`, `onRefreshRequested` — which is written and tested and is the only
 *    way state gets in. A channel that carried rows would be a second way, and two ways drift.
 *  - **No second authorisation surface.** An event says "something you can see changed". It
 *    does not ship rows that would then need the same scoping the REST routes already do
 *    correctly, in a place where getting it wrong is quiet.
 *  - **It cannot apply out of order.** "Go and re-read" always converges on the server's
 *    current truth. An applied delta can arrive after the state it described was superseded,
 *    and there is nothing in a fan-out channel that would notice.
 *  - **Fan-out is cheap.** Nothing serialises a bookmark list per connected client per
 *    change.
 *
 * The cost is one extra round trip per change, which at this scale is nothing.
 *
 * ── Why every event carries an origin ────────────────────────────────────────
 *
 * A client that writes and then receives its own event must ignore it, or you get write →
 * event → re-read → repaint, fighting whatever the user is still doing. Mid-drag window
 * geometry is the sharp case: the re-read lands during the gesture and snaps the window back
 * under the finger. [ServerEvent.originId] is the mechanism, [LUNAPIN_ORIGIN_HEADER] is how
 * it gets to the server, and using it consistently at every consumer is the work.
 *
 * @see ApiRoutes.EVENTS
 */
package se.soderbjorn.lunapin.clientserver

import kotlin.random.Random
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The header a client stamps every request with, naming itself.
 *
 * `X-` prefixed, which IETF advice has discouraged since RFC 6648 — kept anyway because the
 * alternative for a private, single-application header is inventing an unregistered bare name
 * that reads like a standard one. Nothing outside this codebase parses it.
 *
 * **It is not a credential and must never be treated as one.** Anybody can send any value;
 * all it can do is make the sender ignore an echo it would otherwise have acted on. The
 * session id is the credential, and it travels the way it always did.
 */
const val LUNAPIN_ORIGIN_HEADER: String = "X-LunaPin-Origin"

/**
 * A fresh identity for one client instance — one browser tab, one app process.
 *
 * Per *instance* rather than per device or per session, deliberately: two browser tabs on one
 * account are two clients that genuinely do need to hear each other, and a per-session id
 * would make them silent to one another. It lives in memory and is never stored, because
 * surviving a restart would be worse than useless — a client that reconnected under an old
 * identity would suppress echoes of writes made by whatever wrote them last.
 *
 * `Random` rather than a UUID library: this needs to be unlikely to collide among the handful
 * of clients one person has open, not unique in the universe, and a dependency for that would
 * be a poor trade.
 */
fun newOriginId(): String = Random.nextLong().toULong().toString(16)

/**
 * One thing that changed, from the server to every client of that account.
 *
 * A sealed hierarchy with a `kind` discriminator, so a client older than the server meets an
 * unknown case as a *parse failure it can skip* rather than as a stream it can no longer
 * read — see the decoding note on [ServerEventCodec].
 *
 * @property originId the client that caused it, or null when the server itself did — the
 *   enrichment worker finishing a summary is the case that exists today. **Null is not
 *   "everybody's"**: a null origin matches nobody's own id, so every client re-reads, which is
 *   exactly right for a change no client made.
 */
@Serializable
sealed interface ServerEvent {
    val originId: String?

    /**
     * These bookmarks changed — saved, edited, deleted, or enriched.
     *
     * @property ids what changed. **A list rather than one id** because one act can touch
     *   several: a backfill queues everything that was never tried, and a tidy rewrites the
     *   tags of a whole vocabulary. An open editor compares its own id against this and
     *   ignores a change to a different bookmark; a list ignores nothing and simply re-reads.
     *
     *   **Empty means "the collection changed, and this is not the list of how"** — the
     *   backfill's answer, where naming a thousand ids would be a large message for a client
     *   that is going to re-read the whole collection regardless.
     */
    @Serializable
    @SerialName("bookmarksChanged")
    data class BookmarksChanged(
        val ids: List<Long> = emptyList(),
        override val originId: String? = null,
    ) : ServerEvent

    /**
     * One shell setting changed.
     *
     * @property key one of [UiSettingKeys.persisted]. **The key only, never the value** —
     *   a client that cares about `lunapin.workspace.v1` ignores a font change without ever
     *   holding one, and the blob it does care about it fetches for itself.
     */
    @Serializable
    @SerialName("uiSettingChanged")
    data class UiSettingChanged(
        val key: String,
        override val originId: String? = null,
    ) : ServerEvent

    /**
     * The account's typed preferences changed — the AI switches, the summary length, the
     * tags a save adds.
     *
     * No key, unlike [UiSettingChanged], because these are written as a whole set in one
     * `PUT` rather than one at a time. Naming a field would be inventing a granularity the
     * route does not have.
     */
    @Serializable
    @SerialName("accountSettingsChanged")
    data class AccountSettingsChanged(
        override val originId: String? = null,
    ) : ServerEvent
}

/**
 * The one JSON both ends use for [ServerEvent].
 *
 * Here rather than configured at each end, so the discriminator cannot be spelled two ways.
 * `classDiscriminator = "kind"` matches `WorkspaceCodec`'s choice and for its reason: kotlinx's
 * default `type` would collide the day an event gains a field called `type`.
 *
 * **[decode] answers null rather than throwing**, and that is the forward-compatibility
 * promise. A server newer than this client will eventually send a case this client has never
 * heard of; the honest response is to skip that one line and keep reading the stream, not to
 * tear down a connection that is still carrying events it does understand. The cost is that a
 * client can silently miss a kind of change it was never taught about — which is a client that
 * predates the feature, and which would have missed it anyway.
 */
object ServerEventCodec {
    private val json = kotlinx.serialization.json.Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        classDiscriminator = "kind"
    }

    /** Render one event as the line the SSE frame carries. */
    fun encode(event: ServerEvent): String = json.encodeToString(event)

    /** Parse one, or null for anything this client cannot read. */
    fun decode(text: String): ServerEvent? =
        runCatching { json.decodeFromString<ServerEvent>(text) }.getOrNull()
}
